import React, { useState } from 'react';
import { formatApiError } from '../utils/formatApiError';
import { useNavigate, Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Trophy, Mail, Lock, User, Chrome } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { login } from '../services/api';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast({
        title: 'Fout',
        description: 'Vul alle velden in',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);
    try {
      const response = await login(email, password);
      const username = response.user?.username || email;
      toast({
        title: 'Succesvol ingelogd!',
        description: `Welkom terug, ${username}!`,
      });
      navigate('/');
    } catch (error) {
      toast({
        title: 'Fout',
        description: formatApiError(error, 'Login mislukt'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a2332] via-[#2d3e50] to-[#1a2332] flex items-center justify-center px-4 py-12">
      <Card className="w-full max-w-md shadow-2xl border-2 border-[#FDB913]">
        <CardHeader className="text-center">
          <div className="flex justify-center mb-4">
            <div className="bg-[#FDB913] rounded-full p-4">
              <Trophy size={40} className="text-[#1a2332]" />
            </div>
          </div>
          <CardTitle className="text-2xl font-bold text-foreground">Inloggen</CardTitle>
          <CardDescription>Log in op je Formule 1 Pool account</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Label htmlFor="email" className="flex items-center space-x-2 mb-2">
                <Mail size={16} />
                <span>E-mailadres</span>
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="Vul je e-mailadres in"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="border-border focus:border-[#FDB913] focus:ring-[#FDB913]"
                data-testid="login-email"
              />
            </div>

            <div>
              <Label htmlFor="password" className="flex items-center space-x-2 mb-2">
                <Lock size={16} />
                <span>Wachtwoord</span>
              </Label>
              <Input
                id="password"
                type="password"
                data-testid="login-password-input"
                placeholder="Vul je wachtwoord in"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="border-border focus:border-[#FDB913] focus:ring-[#FDB913]"
              />
            </div>

            <Button
              type="submit"
              data-testid="login-submit-button"
              className="w-full bg-[#FDB913] text-[#1a2332] hover:bg-[#e5a911] font-bold text-lg py-6"
              disabled={loading}
            >
              {loading ? 'Bezig met inloggen...' : 'Inloggen'}
            </Button>
          </form>

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-border"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-background text-muted-foreground">Of log in met</span>
            </div>
          </div>

          <Button
            type="button"
            variant="outline"
            className="w-full border-border hover:bg-secondary"
            onClick={handleGoogleLogin}
            data-testid="google-login-btn"
          >
            <Chrome size={20} className="mr-2" />
            Google
          </Button>

          <div className="mt-4 text-center">
            <Link to="/forgot-password" className="text-sm text-muted-foreground hover:text-[#FDB913]">
              Wachtwoord vergeten?
            </Link>
          </div>

          <div className="mt-4 text-center text-sm">
            <span className="text-muted-foreground">Nog geen account? </span>
            <Link to="/register" className="text-[#FDB913] font-semibold hover:underline">
              Registreer hier
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
